import { supabase } from '../lib/supabase';
import { getCurrentOrgId } from '../lib/getCurrentOrgId';

/**
 * organizationTradesService
 * Manages the configurable trade list per organization.
 * RLS enforces org scoping + admin-only writes.
 */

function toCamelCase(obj) {
  if (!obj) return obj;
  const out = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase());
    out[camelKey] = obj?.[key];
  }
  return out;
}

const organizationTradesService = {
  /**
   * Fetch active trades for the current org, ordered by order_index then name.
   * @returns {Promise<Array<{id, organizationId, name, displayName, orderIndex, isActive, createdAt}>>}
   */
  async getByOrg() {
    const orgId = await getCurrentOrgId();
    if (!orgId) return [];

    const { data, error } = await supabase?.from('organization_trades')?.select('id, organization_id, name, display_name, order_index, is_active, created_at')?.eq('organization_id', orgId)?.eq('is_active', true)?.order('order_index', { ascending: true })?.order('name', { ascending: true });

    if (error) {
      console.error('[organizationTradesService.getByOrg]', error);
      return [];
    }

    return (data || [])?.map(toCamelCase);
  },

  /**
   * Create a new trade for the current org (admin only — enforced by RLS).
   * @param {{ name: string, orderIndex?: number }} payload
   */
  async create({ name, orderIndex = 0 }) {
    const orgId = await getCurrentOrgId();
    if (!orgId) throw new Error('No organization found for current user.');

    const { data, error } = await supabase?.from('organization_trades')?.insert({
        organization_id: orgId,
        name: name?.trim(),
        order_index: orderIndex,
        is_active: true,
      })?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  /**
   * Update an existing trade (admin only — enforced by RLS).
   * @param {string} id
   * @param {{ name?: string, orderIndex?: number, isActive?: boolean }} payload
   */
  async update(id, { name, orderIndex, isActive }) {
    const updates = {};
    if (name !== undefined) updates.name = name?.trim();
    if (orderIndex !== undefined) updates.order_index = orderIndex;
    if (isActive !== undefined) updates.is_active = isActive;

    const { data, error } = await supabase?.from('organization_trades')?.update(updates)?.eq('id', id)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  /**
   * Delete a trade (admin only — enforced by RLS).
   * @param {string} id
   */
  async delete(id) {
    const { error } = await supabase?.from('organization_trades')?.delete()?.eq('id', id);

    if (error) throw error;
  },
};

export default organizationTradesService;
